/**
 * MarkdownContentRenderer
 * Safely renders markdown content using react-markdown (for TSG docs, automation scripts)
 */

import { makeStyles, tokens, mergeClasses } from '@fluentui/react-components'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

const useStyles = makeStyles({
  root: {
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      marginTop: tokens.spacingVerticalM,
      marginBottom: tokens.spacingVerticalS,
      fontWeight: tokens.fontWeightSemibold,
    },
    '& h1': { fontSize: tokens.fontSizeBase500 },
    '& h2': { fontSize: tokens.fontSizeBase400 },
    '& h3': { fontSize: tokens.fontSizeBase300 },
    '& p': {
      marginTop: 0,
      marginBottom: tokens.spacingVerticalS,
    },
    '& ul, & ol': {
      marginTop: 0,
      marginBottom: tokens.spacingVerticalS,
      paddingLeft: tokens.spacingHorizontalL,
    },
    '& li': {
      marginBottom: tokens.spacingVerticalXS,
    },
    '& code': {
      fontFamily: 'monospace',
      backgroundColor: tokens.colorNeutralBackground3,
      padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
      borderRadius: tokens.borderRadiusSmall,
    },
    '& pre': {
      backgroundColor: tokens.colorNeutralBackground3,
      padding: tokens.spacingVerticalM,
      borderRadius: tokens.borderRadiusMedium,
      overflow: 'auto',
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
      },
    },
    '& a': {
      color: tokens.colorBrandForeground1,
      textDecoration: 'none',
      ':hover': {
        textDecoration: 'underline',
      },
    },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      marginBottom: tokens.spacingVerticalS,
    },
    '& th, & td': {
      border: `1px solid ${tokens.colorNeutralStroke2}`,
      padding: tokens.spacingVerticalS,
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: tokens.colorNeutralBackground3,
      fontWeight: tokens.fontWeightSemibold,
    },
  },
})

export interface MarkdownContentRendererProps {
  content: string
  className?: string
}

export function MarkdownContentRenderer({ content, className }: MarkdownContentRendererProps) {
  const styles = useStyles()

  return (
    <div className={mergeClasses(styles.root, className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </div>
  )
}
